import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from '../config/api';
import { API_URL } from '../config/api';
import Logo from '../components/Logo';
import { MdHowToVote, MdAccessTime, MdShare, MdContentCopy, MdCheck, MdError } from 'react-icons/md';
import { FaXTwitter, FaFacebook } from 'react-icons/fa6';

function SnapshotPollPage() {
  const { snapshotId } = useParams();
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorType, setErrorType] = useState(null); // 'expired' or 'notfound'
  const [copied, setCopied] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);

  // Generate OG meta tag content for snapshots
  const getOGTitle = () => {
    if (!snapshot) return 'CIVOTE Poll Snapshot';
    return snapshot.poll?.question || 'CIVOTE Poll Snapshot';
  };

  const getOGDescription = () => {
    if (!snapshot) return 'View a point-in-time snapshot of poll results on CIVOTE';
    const yesPercent = snapshot.results?.yesPercent || 0;
    const noPercent = snapshot.results?.noPercent || 0;
    const total = snapshot.results?.totalVotes || 0;
    if (total > 0) {
      return `${yesPercent}% Yes, ${noPercent}% No (${total} votes at time of snapshot) - View on CIVOTE`;
    }
    return 'View this poll snapshot on CIVOTE';
  };

  const getOGUrl = () => {
    return `${window.location.origin}/poll/snapshot/${snapshotId}`;
  };

  useEffect(() => {
    loadSnapshot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotId]);

  const loadSnapshot = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/snapshots/${snapshotId}`);
      setSnapshot(response.data);
    } catch (err) {
      console.error('Failed to load snapshot:', err);
      if (err.response?.status === 410) {
        setErrorType('expired');
        setError('This poll snapshot has expired and is no longer available.');
      } else {
        setErrorType('notfound');
        setError('This poll snapshot was not found.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = window.location.href;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleShareTwitter = () => {
    const text = `Check out this poll: "${snapshot?.snapshot?.question?.substring(0, 80)}..." (Results as of ${formatDate(snapshot?.snapshot?.snapshotAt)})`;
    const url = window.location.href;
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
    window.open(twitterUrl, '_blank', 'width=550,height=420');
    setShowShareMenu(false);
  };

  const handleShareFacebook = () => {
    const text = `Check out this poll: "${snapshot?.snapshot?.question?.substring(0, 80)}..." (Results as of ${formatDate(snapshot?.snapshot?.snapshotAt)})`;
    const url = window.location.href;
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`;
    window.open(facebookUrl, '_blank', 'width=550,height=420');
    setShowShareMenu(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-civote flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-civote flex items-center justify-center p-4">
        <div className="bg-white rounded-card shadow-card max-w-md w-full p-8 text-center">
          <Logo size="large" />
          {errorType === 'expired' ? (
            <>
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mt-4">
                <MdAccessTime className="text-3xl text-orange-500" />
              </div>
              <h1 className="text-2xl font-bold text-gray-800 mt-4 font-rubik">Snapshot Expired</h1>
              <p className="text-gray-600 mt-2 font-rubik">{error}</p>
              <p className="text-sm text-gray-400 mt-2 font-rubik">Poll snapshots are available for 3 months after creation.</p>
            </>
          ) : (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mt-4">
                <MdError className="text-3xl text-red-500" />
              </div>
              <h1 className="text-2xl font-bold text-gray-800 mt-4 font-rubik">Snapshot Not Found</h1>
              <p className="text-gray-600 mt-2 font-rubik">{error}</p>
            </>
          )}
          <Link 
            to="/" 
            className="btn-primary mt-6 inline-block"
            data-testid="go-home-button"
          >
            Go to CIVOTE
          </Link>
        </div>
      </div>
    );
  }

  const pollData = snapshot?.snapshot;
  const results = pollData?.results;
  const yesPercentage = results?.yes?.percentage || 0;
  const noPercentage = results?.no?.percentage || 0;

  return (
    <div className="min-h-screen bg-gradient-civote">
      {/* Open Graph Meta Tags for Social Sharing */}
      <Helmet>
        <title>{getOGTitle()} | CIVOTE Snapshot</title>
        <meta name="description" content={getOGDescription()} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={getOGUrl()} />
        <meta property="og:title" content={getOGTitle()} />
        <meta property="og:description" content={getOGDescription()} />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_votesphere-6/artifacts/suvvb8kr_App%20icon.png" />
        <meta property="og:site_name" content="CIVOTE" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={getOGUrl()} />
        <meta name="twitter:title" content={getOGTitle()} />
        <meta name="twitter:description" content={getOGDescription()} />
        <meta name="twitter:image" content="https://customer-assets.emergentagent.com/job_votesphere-6/artifacts/suvvb8kr_App%20icon.png" />
      </Helmet>

      {/* Header */}
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/">
              <Logo />
            </Link>
          </div>
          <div className="flex items-center space-x-3">
            <Link 
              to="/" 
              className="btn-primary text-sm px-4 py-2"
              data-testid="join-civote-button"
            >
              Join CIVOTE
            </Link>
          </div>
        </div>
      </nav>

      {/* Poll Content */}
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="card-container" data-testid="snapshot-poll-card">
          
          {/* Timestamp Disclaimer Banner */}
          <div className="bg-blue-600 text-white rounded-lg p-4 mb-5 -mx-2 -mt-2">
            <div className="flex items-start gap-3">
              <MdAccessTime className="text-2xl flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-base font-rubik">
                  Results as of {formatDate(pollData?.snapshotAt)}
                </p>
                <p className="text-blue-100 text-sm mt-1 font-rubik">
                  This is a snapshot of poll results at the time it was shared. Current results may differ as more people vote.
                </p>
              </div>
            </div>
          </div>

          {/* Poll Question */}
          <h2 className="text-2xl font-bold text-gray-800 font-rubik mb-3" data-testid="snapshot-question">
            {pollData?.question}
          </h2>

          {/* Tags */}
          {pollData?.tagDetails && pollData.tagDetails.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {pollData.tagDetails.map((tag, index) => (
                <span
                  key={index}
                  className="tag-pill text-white font-rubik text-xs"
                  style={{ backgroundColor: tag.color }}
                >
                  {tag.name}
                </span>
              ))}
            </div>
          )}

          {/* Results Display */}
          <div className="mt-6" data-testid="snapshot-results">
            <div className="space-y-4">
              {/* Yes Bar */}
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700 font-rubik">Yes</span>
                  <span className="text-sm font-semibold text-primary font-rubik">
                    {yesPercentage}% ({results?.yes?.count || 0} votes)
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-5 overflow-hidden">
                  <div 
                    className="bg-primary h-full rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                    style={{ width: `${Math.max(yesPercentage, 5)}%` }}
                  >
                    {yesPercentage >= 15 && (
                      <span className="text-xs font-bold text-white">{yesPercentage}%</span>
                    )}
                  </div>
                </div>
              </div>

              {/* No Bar */}
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700 font-rubik">No</span>
                  <span className="text-sm font-semibold text-danger font-rubik">
                    {noPercentage}% ({results?.no?.count || 0} votes)
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-5 overflow-hidden">
                  <div 
                    className="bg-danger h-full rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                    style={{ width: `${Math.max(noPercentage, 5)}%` }}
                  >
                    {noPercentage >= 15 && (
                      <span className="text-xs font-bold text-white">{noPercentage}%</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Total Responses */}
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-gray-500 font-rubik">
                Total Responses: <span className="font-semibold text-gray-700">{results?.totalResponses || 0}</span>
              </p>
              
              {/* Share Options */}
              <div className="relative">
                <button
                  onClick={() => setShowShareMenu(!showShareMenu)}
                  className="flex items-center gap-1.5 text-sm text-tertiary hover:text-primary font-medium font-rubik transition-colors"
                  data-testid="reshare-button"
                >
                  <MdShare className="text-base" />
                  Share
                </button>
                
                {showShareMenu && (
                  <div className="absolute right-0 bottom-full mb-2 bg-white rounded-lg shadow-xl border border-gray-200 py-2 w-48 z-10">
                    <button
                      onClick={handleCopyLink}
                      className="w-full px-3 py-2 flex items-center gap-2 hover:bg-gray-50 transition-colors text-left"
                    >
                      {copied ? <MdCheck className="text-primary" /> : <MdContentCopy className="text-gray-500" />}
                      <span className="text-sm font-rubik">{copied ? 'Copied!' : 'Copy link'}</span>
                    </button>
                    <button
                      onClick={handleShareTwitter}
                      className="w-full px-3 py-2 flex items-center gap-2 hover:bg-gray-50 transition-colors text-left"
                    >
                      <FaXTwitter className="text-black" />
                      <span className="text-sm font-rubik">Share on X</span>
                    </button>
                    <button
                      onClick={handleShareFacebook}
                      className="w-full px-3 py-2 flex items-center gap-2 hover:bg-gray-50 transition-colors text-left"
                    >
                      <FaFacebook className="text-[#4267B2]" />
                      <span className="text-sm font-rubik">Share on Facebook</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* CTA to Vote */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="bg-green-50 border border-primary rounded-lg p-4 text-center">
              <MdHowToVote className="text-3xl text-primary mx-auto mb-2" />
              <h3 className="text-lg font-semibold text-gray-800 font-rubik mb-1">
                Want to vote on this poll?
              </h3>
              <p className="text-sm text-gray-600 font-rubik mb-3">
                Join CIVOTE to participate in polls and see live results!
              </p>
              <Link 
                to="/" 
                className="btn-primary inline-block px-6"
                data-testid="join-to-vote-button"
              >
                Join CIVOTE Free
              </Link>
            </div>
          </div>
        </div>

        {/* View Stats */}
        {snapshot?.viewCount > 0 && (
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-400 font-rubik">
              This snapshot has been viewed {snapshot.viewCount} time{snapshot.viewCount !== 1 ? 's' : ''}
            </p>
          </div>
        )}

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-gray-500 text-sm font-rubik">
            Powered by <span className="font-semibold text-primary">CIVOTE</span> - Your voice matters
          </p>
        </div>
      </div>
    </div>
  );
}

export default SnapshotPollPage;
